import { createSelector } from '@reduxjs/toolkit';
import _ from 'lodash';
import { createIntl } from 'react-intl';
import { all, put, select, takeEvery } from 'redux-saga/effects';
import { toastService } from 'components/molecules/toast';
import { logger } from 'services';
import { getTranslationCache } from 'utils/translation-loader';
import { deleteTaskInProjectTemplate, updateAutomationByIdAction } from '../../actions';
import { selectTemplateTask } from '../../selectors';
import { deleteProjectTemplateSubTask, deleteProjectTemplateTask, setProjectTemplateActiveRecord } from '../../slices';
const intl = createIntl({
    locale: 'en',
    messages: getTranslationCache().get('en') || {}
});
const notifyDeleteFailed = ()=>{
    toastService({
        title: intl.formatMessage({
            id: 'task.delete.failed_title'
        }),
        message: intl.formatMessage({
            id: 'task.delete.failed_message'
        })
    }).error();
};
const selectActiveRecord = createSelector((state)=>state.templates.project.activeRecord, (activeRecord)=>activeRecord);
const selectAutomationsInActiveTemplate = (state)=>{
    var _ref;
    var _state_templates_project_data_activeId_template, _state_templates_project_data_activeId;
    const { activeId } = state.templates.project;
    return (_ref = (_state_templates_project_data_activeId = state.templates.project.data[activeId]) === null || _state_templates_project_data_activeId === void 0 ? void 0 : (_state_templates_project_data_activeId_template = _state_templates_project_data_activeId.template) === null || _state_templates_project_data_activeId_template === void 0 ? void 0 : _state_templates_project_data_activeId_template.automations) !== null && _ref !== void 0 ? _ref : [];
};
function* getAutomationsForTask(taskId) {
    const automations = yield select(selectAutomationsInActiveTemplate);
    return automations.filter((e)=>{
        var _e_tags;
        return ((_e_tags = e.tags) === null || _e_tags === void 0 ? void 0 : _e_tags.taskTemplateInstanceReferenceId) === taskId && !e.deleted;
    });
}
function* markAutomationsAsDeletedForTask(taskId) {
    const automationsForTask = yield getAutomationsForTask(taskId);
    yield all(automationsForTask.map((automation)=>put(updateAutomationByIdAction({
            automation: _.cloneDeep({
                ...automation,
                deleted: true
            }),
            automationId: automation.automationId
        }))));
}
export function* isSameTaskOrChildTask(task, activeTaskId) {
    // Stale / missing task refs must not throw reading 'taskId'.
    if (!task) {
        return false;
    }
    if (task.taskId === activeTaskId) {
        return true;
    }
    const { subTasks } = task;
    if (subTasks) {
        for(let i = 0; i < subTasks.length; i += 1){
            if (activeTaskId === subTasks[i]) {
                return true;
            }
            const subTask = yield select(selectTemplateTask(subTasks[i]));
            if (yield isSameTaskOrChildTask(subTask, activeTaskId)) {
                return true;
            }
        }
    }
    return false;
}
function* deleteTaskInTemplateSaga(action) {
    try {
        var _action_payload, _task_parentTask;
        const taskId = action === null || action === void 0 ? void 0 : (_action_payload = action.payload) === null || _action_payload === void 0 ? void 0 : _action_payload.taskId;
        if (taskId == null || Number.isNaN(taskId)) {
            logger.error('deleteTaskInTemplateSaga missing taskId', {
                error: new Error('missing taskId'),
                payload: action === null || action === void 0 ? void 0 : action.payload
            });
            notifyDeleteFailed();
            return;
        }
        const task = yield select(selectTemplateTask(taskId));
        // Already gone (bulk parent cascade / double-delete) — not a user-facing
        // failure; toasting here spam-errors successful multi-select deletes.
        if (!task) {
            logger.warn('deleteTaskInTemplateSaga task not found', {
                taskId
            });
            return;
        }
        const activeRecord = yield select(selectActiveRecord);
        const shouldChangeActiveRecord = (activeRecord === null || activeRecord === void 0 ? void 0 : activeRecord.type) === 'TASK' ? yield isSameTaskOrChildTask(task, activeRecord.id) : false;
        // Mark any automations associated with this task as deleted
        // This prevents save failures when the task is removed but automations still reference it
        yield markAutomationsAsDeletedForTask(taskId);
        if (task.projectPhase) {
            yield put(deleteProjectTemplateTask({
                taskId
            }));
            if (shouldChangeActiveRecord) {
                yield put(setProjectTemplateActiveRecord({
                    id: task.projectPhase.projectPhaseId,
                    type: 'PROJECT_PHASE'
                }));
            }
        } else if (((_task_parentTask = task.parentTask) === null || _task_parentTask === void 0 ? void 0 : _task_parentTask.taskId) != null) {
            yield put(deleteProjectTemplateSubTask({
                taskId
            }));
            if (shouldChangeActiveRecord) {
                yield put(setProjectTemplateActiveRecord({
                    id: task.parentTask.taskId,
                    type: 'TASK'
                }));
            }
        } else {
            logger.error('deleteTaskInTemplateSaga task has no phase or parent', {
                error: new Error('task has no phase or parent'),
                taskId
            });
            notifyDeleteFailed();
        }
    } catch (error) {
        logger.error('deleteTaskInTemplateSaga failed ', {
            error
        });
        notifyDeleteFailed();
    }
}
export function* watchDeleteTaskInProjectTemplateSaga() {
    // takeEvery: bulk-delete dispatches one action per id; takeLatest cancelled
    // all but the last so multi-select delete silently dropped most tasks.
    yield takeEvery(deleteTaskInProjectTemplate.type, deleteTaskInTemplateSaga);
}
